import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  MessageCircle,
  MessageSquare,
  Phone,
  Mail,
  ChevronDown,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';

import { useApiData } from '../hooks/useApiData.js';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { listConversations } from '../services/api.js';
import {
  conversations as seedConversations,
  leadById,
  agentById,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);

// ── channel metadata ─────────────────────────────────────────────────────────

const CHANNEL_META = {
  webchat: {
    label: 'Webchat',
    Icon: Globe,
    cls: 'bg-[#DBEAFE] text-[#2563EB]',
  },
  sms: {
    label: 'SMS',
    Icon: MessageSquare,
    cls: 'bg-[#CCFBF1] text-[#0F766E]',
  },
  whatsapp: {
    label: 'WhatsApp',
    Icon: MessageCircle,
    cls: 'bg-[#DCFCE7] text-[#15803D]',
  },
  voice: {
    label: 'Voice',
    Icon: Phone,
    cls: 'bg-[#FFEDD5] text-[#C2410C]',
  },
  email: {
    label: 'Email',
    Icon: Mail,
    cls: 'bg-[#E0E7FF] text-[#4338CA]',
  },
};

function channelMeta(channel) {
  return (
    CHANNEL_META[channel] || {
      label: channel,
      Icon: MessageSquare,
      cls: 'bg-[#F3F4F6] text-[#6B7280]',
    }
  );
}

// ── language flags ───────────────────────────────────────────────────────────

const LANGUAGE_META = {
  es: { flag: '🇪🇸', label: 'Spanish' },
  en: { flag: '🇺🇸', label: 'English' },
  pt: { flag: '🇧🇷', label: 'Portuguese' },
  fr: { flag: '🇫🇷', label: 'French' },
};

function languageMeta(lang) {
  return LANGUAGE_META[lang] || { flag: '🌐', label: lang || 'Unknown' };
}

// ── status derivation from outcome ───────────────────────────────────────────

const STATUS_META = {
  active: { label: 'Active', cls: 'bg-[#FEF3C7] text-[#D97706]' },
  completed: { label: 'Completed', cls: 'bg-[#DCFCE7] text-[#16A34A]' },
  escalated: { label: 'Escalated', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
};

function deriveStatus(conv) {
  if (conv.escalation) return 'escalated';
  if (conv.outcome === 'escalated_to_human') return 'escalated';
  if (conv.outcome === 'flagged_for_review') return 'escalated';
  if (conv.outcome === 'nurturing') return 'active';
  return 'completed';
}

// ── time helpers ─────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMin = Math.round((NOW - then) / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'Yesterday';
  if (diffD < 7) return `${diffD} days ago`;
  if (diffD < 30) return `${Math.round(diffD / 7)}w ago`;
  if (diffD < 365) return `${Math.round(diffD / 30)}mo ago`;
  return `${Math.round(diffD / 365)}y ago`;
}

function formatBubbleTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ── helpers ──────────────────────────────────────────────────────────────────

function truncate(text, max) {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function lastLeadPreview(messages) {
  if (!messages || messages.length === 0) return '';
  const last = messages[messages.length - 1];
  return last.text || '';
}

// ── sub-components ───────────────────────────────────────────────────────────

function ChannelBadge({ channel }) {
  const meta = channelMeta(channel);
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-badge px-2 py-0.5 text-xs font-medium ${meta.cls}`}
    >
      <Icon size={12} /> {meta.label}
    </span>
  );
}

function LanguageFlag({ lang }) {
  const meta = languageMeta(lang);
  return (
    <span
      title={meta.label}
      aria-label={meta.label}
      className="inline-flex items-center justify-center text-base leading-none"
    >
      {meta.flag}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.completed;
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function ChatBubble({ message }) {
  const isLead = message.from === 'lead';
  const isAgent = message.from === 'agent';
  const author = isLead
    ? 'Lead'
    : isAgent
      ? agentById[message.agentId]?.name || 'Agent'
      : 'AI';

  if (isLead) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[70%]">
          <div
            className="rounded-[12px] px-4 py-2.5 text-text-primary"
            style={{ backgroundColor: '#F5F5F5', fontSize: '15px', lineHeight: 1.6 }}
          >
            {message.text}
          </div>
          <div className="mt-1 flex items-center justify-end gap-2 text-[11px] text-text-secondary">
            <span>{author}</span>
            <span>·</span>
            <span>{formatBubbleTime(message.at)}</span>
          </div>
        </div>
      </div>
    );
  }

  // AI or agent → left-aligned, orange accent
  const accent = isAgent ? '#2563EB' : '#EA580C';
  const bg = isAgent ? 'rgba(37,99,235,0.08)' : 'rgba(234,88,12,0.08)';
  return (
    <div className="flex justify-start">
      <div className="max-w-[75%]">
        <div
          className="rounded-[12px] px-4 py-2.5 text-text-primary"
          style={{ backgroundColor: bg, borderLeft: `3px solid ${accent}`, fontSize: '15px', lineHeight: 1.6 }}
        >
          {message.text}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-text-secondary">
          <span style={{ color: accent }} className="font-medium">
            {author}
          </span>
          <span>·</span>
          <span>{formatBubbleTime(message.at)}</span>
        </div>
      </div>
    </div>
  );
}

function Transcript({ conversation }) {
  const lead = leadById[conversation.leadId] || conversation.lead || null;
  return (
    <div className="bg-bg-subtle border-t border-border-card px-4 md:px-6 py-5 space-y-4">
      {conversation.summary && (
        <div className="rounded-btn border border-border-card bg-white px-4 py-3 text-sm text-text-secondary">
          <span className="font-medium text-text-primary">Summary · </span>
          {conversation.summary}
        </div>
      )}
      {conversation.escalation && (
        <div className="rounded-btn border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B]">
          <span className="font-medium">Escalation · </span>
          {conversation.escalation.trigger}
        </div>
      )}
      <div className="space-y-3">
        {conversation.messages.map((m, i) => (
          <ChatBubble key={`${conversation.id}-m-${i}`} message={m} />
        ))}
      </div>
      {lead && (
        <div className="pt-2">
          <Link
            to={`/leads/${lead.id}`}
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover font-medium"
          >
            View {lead.name}’s profile
            <ChevronRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'escalated', label: 'Escalated' },
];

const CHANNEL_FILTER_OPTIONS = [
  { value: 'all', label: 'All channels' },
  { value: 'webchat', label: 'Webchat' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'voice', label: 'Voice' },
  { value: 'email', label: 'Email' },
];

export default function Conversations() {
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const conversationsReq = useApiData(() => listConversations({ limit: 200 }), []);
  const apiConversations = conversationsReq.data?.conversations || [];
  const conversations = apiConversations.length ? apiConversations : seedConversations;

  const rows = useMemo(() => {
    const enriched = conversations.map((c) => ({
      conversation: c,
      // Seed id → seed lead; else fall back to the denormalized nested
      // lead the API attaches so live rows don't render "Unknown lead".
      lead: leadById[c.leadId] || c.lead || null,
      status: deriveStatus(c),
      lastAt: c.endedAt || c.startedAt,
      preview: lastLeadPreview(c.messages),
    }));
    return enriched.sort(
      (a, b) => new Date(b.lastAt) - new Date(a.lastAt),
    );
  }, [conversations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(({ conversation, lead, status }) => {
      if (channelFilter !== 'all' && conversation.channel !== channelFilter) return false;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (q) {
        const hay = `${lead?.name || ''} ${conversation.summary || ''} ${conversation.messages
          .map((m) => m.text)
          .join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, channelFilter, statusFilter]);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="page-title mb-0">Conversations</h1>
            {conversationsReq.loading && <InlineSpinner />}
          </div>
          <p className="page-subtitle mb-0 mt-2">
            Every AI-led conversation across SMS, WhatsApp, voice, and webchat.
            Click any row to open the full transcript.
          </p>
        </div>
        <span className="text-sm text-text-secondary">
          Showing {filtered.length} of {rows.length}
        </span>
      </header>

      {conversationsReq.error && (
        <ErrorBanner
          message="Couldn’t load conversations from the server."
          onRetry={conversationsReq.refetch}
        />
      )}

      {/* Filter bar */}
      <div className="card p-6 md:px-7 md:py-6 space-y-3 w-full">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by lead name or message content"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: '15px' }}
            className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                       placeholder:text-text-secondary pl-9 pr-9 py-2
                       hover:border-accent/60 focus:border-accent focus:outline-none
                       focus:ring-1 focus:ring-accent/50 transition-colors"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="sr-only">Channel</span>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              aria-label="Channel"
              style={{ fontSize: '15px' }}
              className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                         px-3 py-2 hover:border-accent/60 focus:border-accent focus:outline-none
                         focus:ring-1 focus:ring-accent/50 transition-colors"
            >
              {CHANNEL_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className="sr-only">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Status"
              style={{ fontSize: '15px' }}
              className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                         px-3 py-2 hover:border-accent/60 focus:border-accent focus:outline-none
                         focus:ring-1 focus:ring-accent/50 transition-colors"
            >
              {STATUS_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-base text-text-primary font-medium">
            No conversations match your filters
          </p>
          <p className="mt-1 text-sm text-text-secondary">
            Try broadening your search or clearing the channel/status filters.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden w-full">
          <ul className="divide-y divide-border-card">
            {filtered.map(({ conversation, lead, status, lastAt, preview }) => {
              const isOpen = expandedId === conversation.id;
              return (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={() => toggle(conversation.id)}
                    className="w-full text-left px-4 md:px-6 py-4 hover:bg-bg-subtle transition-colors
                               flex items-start gap-3 md:gap-4"
                    aria-expanded={isOpen}
                  >
                    <span className="pt-1 text-text-secondary">
                      {isOpen ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="font-semibold text-text-primary truncate">
                          {lead?.name || 'Unknown lead'}
                        </span>
                        <ChannelBadge channel={conversation.channel} />
                        <LanguageFlag lang={conversation.language} />
                        <StatusBadge status={status} />
                      </div>
                      <p className="mt-1 text-sm text-text-secondary truncate">
                        {truncate(preview, 80)}
                      </p>
                    </div>
                    <span className="text-xs text-text-secondary whitespace-nowrap shrink-0 pt-1">
                      {relativeTime(lastAt)}
                    </span>
                  </button>
                  {isOpen && <Transcript conversation={conversation} />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
